<template>
  <div id="login-div">

    <h1 class="page-title mt-2">
      {{ $t('loginTitle') }}
    </h1>
    <h2 class="content mt-3">
      {{ $t('loginSubtitle') }}
    </h2>

    <form @submit.stop.prevent="handleSubmit" class="mt-5">

      <b-alert :show.sync="ui.showError" variant="danger">
        {{ ui.showErrorMessage }}
        <a class="error-danger-link" href="#" v-on:click="resendValidateAccountMail" v-if="ui.showValidateAccountError">
          {{$t('account.not.validated.resent.error')}}
        </a>
      </b-alert>

      <b-form-group
        :label="$t('loginFirstCredential')"
        label-for="username-input">
        <b-form-input
          id="username-input"
          v-model="form.username.value"
          type="email"
          required
          @keydown="hideError">
        </b-form-input>
      </b-form-group>

      <b-form-group
        :label="$t('loginSecondCredential')"
        label-for="password-input">
        <b-input-group>
          <b-form-input
            ref="password"
            id="password-input"
            class="form-input-with-right-append-inside"
            v-model="form.password.value"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="off"
            required
            @keydown="hideError">
          </b-form-input>
          <b-input-group-append>
            <div class="cursor-pointer custom-eye" @click.prevent="togglePassword">
              <i v-if="!showPassword" class="ph-custom ph-eye-fill"/>
              <i v-else class="ph-custom ph-eye-slash-fill"/>
            </div>
          </b-input-group-append>
        </b-input-group>

        <div class="d-flex justify-content-between mt-1">
          <div>
            <b-form-checkbox
              class="remember-me"
              id="remember_me"
              v-model="form.rememberMe.value">
              {{ $t('Remember.me') }}
            </b-form-checkbox>
          </div>
          <div class="text-right">
            <b-link v-on:click="showForgotPasswordView" class="link-main-color">{{ $t('forgotPasswordText') }}</b-link>
          </div>
        </div>

      </b-form-group>

      <div class="mt-5 d-flex justify-content-center flex-column align-items-center">
        <b-button type="submit" class="btn-block" variant="primary">{{ $t('LogInButtonText') }}</b-button>
        <div class="mt-3 d-flex justify-content-center">
            <span class="content">
              {{$t('Login.no.account')}}
            </span>
          <a class="ml-1 signup-link" @click="showSignUpView">{{$t('Login.goToRegister')}}</a>
        </div>
      </div>

    </form>

  </div>
</template>

<script>

import {mapActions} from 'vuex'
import api from '../../../services/api'

export default {

  name: 'LoginComponent',

  props:{
    username : {
      required : false,
    }
  },

  data () {
    return {
      form: {
        username: {
          value: null
        },
        password: {
          value: null
        },
        rememberMe: {
          value: false
        },
      },
      ui: {
        showError: false,
        showErrorMessage: this.$i18n.t('internal.server.error'),
        showValidateAccountError: false,
      },
      showPassword: false
    }
  },

  watch : {
    'username': {
      immediate:true,
      handler () {
        this.form.username.value = this.username
      }
    },
  },

  emits: ['showForgotPasswordView', 'showSignUpView', 'showSmsCodeView','showValideAccount', 'showRedirectProModal'],

  mounted () {
    this.$refs.password.focus()
  },

  methods: {

    ...mapActions(['LogIn', 'setRememberMe']),

    togglePassword() {
      this.showPassword = !this.showPassword
    },

    async handleSubmit () {
      let data = {
        username: this.form.username.value.trim(),
        password: this.form.password.value
      }

      this.setRememberMe(this.form.rememberMe.value)
      if (this.form.rememberMe.value) {
        data['remember-me'] = true
      }

      let formData = new FormData()
      formData.append('isForIndividual', "true")
      formData.append('username', data['username'])
      formData.append('password', data['password'])

      await api({
        method: 'POST',
        url: '/authentication/beforeLogin',
        data: formData
      })
        .then(async (response) => {
          let displaySmsCodeValidation = response.data.displayCodeValidation
          if (displaySmsCodeValidation) {
            // code sms
            let phoneNumber = Object(response.data)['phoneNumber']
            this.showSMSCodeView(data['username'], data['password'], phoneNumber)
          } else {
            // login
            await this.LogIn(data)
              .then(async () => {
                this.ui.showError = false
                this.goToHome()
              })
              .catch((error) => {
                if (error.toString() === 'Locked') {
                  this.ui.showErrorMessage = this.$i18n.t('Profile.forbidden.error')
                  this.ui.showError = true
                }
              })
          }
        })
        .catch((error) => {
          if (!error.response) {
            console.log("error", error)
          }

          if (error.response && error.response.status === 401) {
            this.ui.showErrorMessage = this.$i18n.t('Login.error')
            this.ui.showError = true
          }
          if (error.response && error.response.status === 403) {
            if(error.response.data.message === 'ResendValidateMobileAccount.success') {
              this.$emit('showValideAccount', this.form.username.value.trim(),this.form.password.value)
            } else {
              this.ui.showErrorMessage = this.$i18n.t(error.response.data.message)
              this.ui.showError = true
              if (error.response.data.message === 'account.not.validated.error') {
                this.ui.showValidateAccountError = true
              }
            }
          }
          if (error.response && error.response.status === 423) {
            this.ui.showErrorMessage = this.$i18n.t('account.locked.error')
            this.ui.showError = true
          }

          if (error.response && error.response.status === 468) {
            //PRO
            this.$emit('showRedirectProModal',this.form.username.value.trim())
          }
          if (error.response && error.response.status === 467) {
            //OTHERS
            this.ui.showErrorMessage = this.$i18n.t('Login.error')
            this.ui.showError = true
          }

          if (error.response && error.response.status === 500) {
            this.ui.showErrorMessage = this.$i18n.t('internal.server.error')
            this.ui.showError = true
          }
        })
    },

    resendValidateAccountMail () {
      this.hideError()

      let formData = new FormData()
      formData.append('username', this.form.username.value.trim())

      api({
        method: 'POST',
        url: `/authentication/resendValidateAccountMailCode`,
        data: formData
      })
        .then(() => {
          this.$emit('showValideAccount', this.form.username.value.trim(),this.form.password.value)
        })
        .catch((error) => {
          if (!error.response) {
            console.log("error", error)
          }
          if (error.response && error.response.status === 401) {
            this.ui.showErrorMessage = this.$i18n.t('Login.error')
            this.ui.showError = true
          }
          if (error.response && error.response.status === 500) {
            this.ui.showErrorMessage = this.$i18n.t('internal.server.error')
            this.ui.showError = true
          }
        })
    },

    showForgotPasswordView () {
      this.$emit('showForgotPasswordView')
    },

    showSignUpView () {
      this.$emit('showSignUpView', this.form.username.value ? this.form.username.value.trim() : null)
    },

    showSmsCodeView (username, password, phoneNumber) {
      this.$emit('showSmsCodeView', username, password, phoneNumber)
    },

    hideError () {
      this.ui.showError = false
      this.ui.showErrorMessage = null
      this.ui.showValidateAccountError = false
    },

    hideMessages () {
      this.hideError()
    },

    goToHome () {
      this.$router.push({
        name: 'Home'
      })
    }

  }

}

</script>

<style scoped>

.custom-eye{
  border: 1px solid var(--main-grey-dark) !important;
  border-left: none;
  border-top-right-radius: var(--form-control-border-radius);
  border-bottom-right-radius: var(--form-control-border-radius);
  padding-left: 1em;
  padding-right: 1em;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--main-grey-medium) !important;
  background-color: var(--main-black-medium) !important;
}

</style>



// WEBPACK FOOTER //
// src/views/auth/components/LoginComponent.vue